import { useId, useRef } from 'react';
import { clsx } from 'clsx';
import { Upload } from 'lucide-react';
import { FileIcon } from '@/components/ui/FileIcon';
import {
  ACCEPT_ATTRIBUTE,
  ALLOWED_EXTENSIONS,
  MAX_FILE_BYTES,
  formatMegabytes,
} from './fileReportRules';

interface FileReportFilePickerProps {
  file: File | null;
  onChange: (file: File | null) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
}

export function FileReportFilePicker({
  file,
  onChange,
  label = 'File',
  error,
  disabled = false,
}: FileReportFilePickerProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="w-full">
      <label
        htmlFor={inputId}
        className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
      >
        {label}
        <span className="ml-1 text-red-500">*</span>
      </label>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        disabled={disabled}
        className="sr-only"
        aria-invalid={!!error}
        aria-describedby={`${inputId}-help`}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className={clsx(
          'flex w-full items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-left transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50',
          error
            ? 'border-red-400 dark:border-red-500'
            : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-white/5'
        )}
      >
        {file ? (
          <>
            <FileIcon fileName={file.name} size="sm" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">
                {file.name}
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                {formatMegabytes(file.size)} · click to choose another file
              </span>
            </span>
          </>
        ) : (
          <>
            <Upload className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
            <span className="text-sm text-gray-600 dark:text-gray-300">Choose a file…</span>
          </>
        )}
      </button>
      {error ? (
        <p id={`${inputId}-help`} className="mt-1.5 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : (
        <p id={`${inputId}-help`} className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
          {ALLOWED_EXTENSIONS.join(' ')} · up to {formatMegabytes(MAX_FILE_BYTES)}
        </p>
      )}
    </div>
  );
}
